import React, { FC, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import format from 'date-fns/format'
import isSameDay from 'date-fns/isSameDay'
import isBefore from 'date-fns/isBefore'
import { BasicCard } from 'components/Card'
import { DailyCalendar, MonthlyCalendarContainer, OrderAmount, ToggleCalendarButton } from 'components/Calendar'
import { HorizontalLine, Spacer } from 'components/Utility'
import { BlueButton, OrangeButton } from 'components/Button'
import { State } from 'index'
import { getClosedDates, getCurrentDate } from 'reducks/calendar/selectors'
import { getOrders } from 'reducks/checkout/selectors'
import { OrderType } from 'types/checkout'
import { parseToDate } from 'lib/helper'
import startOfToday from 'date-fns/startOfToday'
import { getAuthShopRef } from 'reducks/auth/selectors'

type Props = {
  calendarType: 'daily' | 'monthly'
  toggleCalendar: () => void
}

const Calendar: FC<Props> = React.memo((props) => {
  const { calendarType, toggleCalendar } = props

  const dispatch = useDispatch()
  const currentDate = useSelector((state: State) => getCurrentDate(state))
  const orders: OrderType[] = useSelector((state: State) => getOrders(state))
  const closedDates = useSelector((state: State) => getClosedDates(state))
  const shopRef = useSelector((state: State) => getAuthShopRef(state))
  const isToday = isSameDay(currentDate, new Date())
  const isPast = isBefore(currentDate, startOfToday())
  const isClose = closedDates.some((closedDate) => isSameDay(currentDate, closedDate))

  // 現在日付の注文数を取得する
  const orderAmount = useMemo(() => {
    const ordersOnDate = orders.filter((order) => isSameDay(parseToDate(order.date), currentDate))
    // 注文数＝受取番号数＝その日に購入したユーザー数と同じ
    const userIds = new Set()
    ordersOnDate.map((order) => userIds.add(order.user_id))
    return userIds.size
  }, [currentDate, orders])

  const goToOrderPage = useCallback(() => {
    dispatch(push(`/order/${format(currentDate, 'yyyyMMdd')}`))
  }, [currentDate])

  const goToScheduledItemPage = useCallback(() => {
    dispatch(push(`/scheduled_item/${format(currentDate, 'yyyyMMdd')}`))
  }, [currentDate])

  return (
    <BasicCard
      buttonComponent={<ToggleCalendarButton calendarType={calendarType} onClick={toggleCalendar} />}
      title={'注文の確認'}
    >
      {calendarType === 'daily' && <DailyCalendar />}
      {calendarType === 'monthly' && <MonthlyCalendarContainer />}
      <HorizontalLine verticalMargin={16} />
      <OrderAmount isToday={isToday} orderAmount={orderAmount} />
      <Spacer size={12} />
      <OrangeButton
        // 注文件数が0の場合、当日の日付を選択していなければ非活性
        disabled={isClose || (orderAmount === 0 && !isToday)}
        label={'注文を確認する'}
        onClick={goToOrderPage}
        size={'medium'}
        type={'main'}
      />
      {/* TODO 店舗横断モードでも必要であれば有効化する */}
      {typeof shopRef === 'undefined' && (
        <>
          <Spacer size={12} />
          <BlueButton
            disabled={isClose}
            label={isPast ? '販売した商品を確認する' : '販売予定の商品を確認する'}
            onClick={goToScheduledItemPage}
            size={'medium'}
            type={'main'}
          />
        </>
      )}
    </BasicCard>
  )
})

export default Calendar
